import { type InputHTMLAttributes, type ReactNode, forwardRef, useId } from 'react';

import { cn } from '../utils/cn';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /**
   * Input label
   */
  label?: string;

  /**
   * Input size
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Error message to display
   */
  error?: string;

  /**
   * Helper text to display below input
   */
  helperText?: string;

  /**
   * Icon to display before input
   */
  iconBefore?: ReactNode;

  /**
   * Icon to display after input
   */
  iconAfter?: ReactNode;

  /**
   * Full width input
   * @default false
   */
  fullWidth?: boolean;

  /**
   * Whether the field is required
   * @default false
   */
  required?: boolean;

  /**
   * Custom container class name
   */
  containerClassName?: string;

  /**
   * Custom label class name
   */
  labelClassName?: string;
}

/**
 * Input component
 *
 * A versatile input component with validation, icons, labels, and multiple states.
 * Supports various input types and comprehensive accessibility.
 *
 * @example
 * ```tsx
 * <Input
 *   label="Email"
 *   type="email"
 *   placeholder="Enter your email"
 *   required
 * />
 *
 * <Input
 *   label="Search"
 *   iconBefore={<SearchIcon />}
 *   placeholder="Search..."
 * />
 *
 * <Input
 *   label="Password"
 *   type="password"
 *   error="Password must be at least 8 characters"
 * />
 * ```
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      size = 'md',
      error,
      helperText,
      iconBefore,
      iconAfter,
      fullWidth = false,
      required = false,
      disabled,
      className,
      containerClassName,
      labelClassName,
      id,
      ...props
    },
    ref
  ) => {
    const autoId = useId();
    const inputId = id || `input-${autoId}`;
    const errorId = `${inputId}-error`;
    const helperTextId = `${inputId}-helper`;

    const baseStyles = [
      'block w-full rounded-lg border',
      'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
      'transition-colors duration-200',
      'focus:outline-none focus:ring-2 focus:ring-offset-1 dark:focus:ring-offset-gray-900',
      'disabled:bg-gray-50 dark:disabled:bg-gray-900 disabled:text-gray-500 dark:disabled:text-gray-600 disabled:cursor-not-allowed',
      'placeholder:text-gray-400 dark:placeholder:text-gray-500',
    ];

    const sizeStyles = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-base',
      lg: 'px-5 py-3 text-lg',
    };

    const iconPaddingStyles = {
      sm: {
        before: 'pl-9',
        after: 'pr-9',
      },
      md: {
        before: 'pl-10',
        after: 'pr-10',
      },
      lg: {
        before: 'pl-12',
        after: 'pr-12',
      },
    };

    const stateStyles = error
      ? [
          'border-error-500',
          'text-error-900 dark:text-error-200',
          'focus:ring-error-500',
          'focus:border-error-500',
        ]
      : [
          'border-gray-300 dark:border-gray-600',
          'focus:ring-primary-500',
          'focus:border-primary-500 dark:focus:border-primary-400',
        ];

    const iconPositionStyles = {
      sm: {
        before: 'left-3',
        after: 'right-3',
      },
      md: {
        before: 'left-3',
        after: 'right-3',
      },
      lg: {
        before: 'left-4',
        after: 'right-4',
      },
    };

    const iconSizeStyles = {
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-6 w-6',
    };

    return (
      <div className={cn(fullWidth ? 'w-full' : '', containerClassName)}>
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1',
              disabled && 'text-gray-500 dark:text-gray-600',
              labelClassName
            )}
          >
            {label}
            {required && <span className="text-error-500 dark:text-error-400 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          {iconBefore && (
            <div
              className={cn(
                'absolute top-1/2 -translate-y-1/2',
                iconPositionStyles[size].before,
                'flex items-center pointer-events-none',
                error ? 'text-error-500 dark:text-error-400' : 'text-gray-400 dark:text-gray-500'
              )}
            >
              <span className={cn(iconSizeStyles[size])}>{iconBefore}</span>
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            disabled={disabled}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? errorId : helperText ? helperTextId : undefined}
            aria-required={required}
            className={cn(
              baseStyles,
              sizeStyles[size],
              stateStyles,
              iconBefore && iconPaddingStyles[size].before,
              iconAfter && iconPaddingStyles[size].after,
              className
            )}
            {...props}
          />

          {iconAfter && (
            <div
              className={cn(
                'absolute top-1/2 -translate-y-1/2',
                iconPositionStyles[size].after,
                'flex items-center pointer-events-none',
                error ? 'text-error-500 dark:text-error-400' : 'text-gray-400 dark:text-gray-500'
              )}
            >
              <span className={cn(iconSizeStyles[size])}>{iconAfter}</span>
            </div>
          )}
        </div>

        {error && (
          <p id={errorId} className="mt-1 text-sm text-error-600 dark:text-error-400" role="alert">
            {error}
          </p>
        )}

        {!error && helperText && (
          <p id={helperTextId} className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
